import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import usePlayerData from '../../hooks/usePlayerData';
import PlayerInfo from "./PlayerInfo/PlayerInfo";
import PlayerStatsDisplay from "./PlayerStats";
import '../../styles/theme.css';
import { Suspense, lazy, useContext, useEffect, useState } from "react";
import { Team } from "../../models/Team";
import TabsComponent from "./PlayerTabs";
import PlayerMatch from "./PlayerMatch";
import SocialMediaButtons from "./SocialMediaLinks";
import Loading from "../../components/Loading";
import { fetchPlayerBySteamIdAndSeason } from "../../services/PlayerStatService";
import { getLatestSeason } from "../../services/MatchService";
import { AuthContext } from "../../context/AuthContext";
import { fetchCurrentTeam } from "../../services/AccountService";
import { admins, currentSeason } from "../../Constants";
import PlayerAchivementsDisplay from "./PlayerAchievement/PlayerAchivements";
import useHasFantasyTeam from '../../hooks/useHasFantasyTeam'; 
import useHasPrediction from "../../hooks/useHasPrediction";
import CompareButton from "./CompareButton"; // Import the new CompareButton component
import useLocalStorage from 'use-local-storage'; // Import useLocalStorage

const PlayerTeams = lazy(() => import("./PlayerTeams"));

const PlayerPage = () => {
    const { steamId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [team, setTeam] = useState<Team | null>();
    const { playerData, loading: playerDataLoading } = usePlayerData(steamId);
    const { user: visitor } = useContext(AuthContext);
    const isLoading = playerDataLoading;
    const [isDark] = useLocalStorage<boolean>("isDark", false);

    const hasFantasyTeam = useHasFantasyTeam(playerData?.steamID);
    const hasPredicted = useHasPrediction(playerData?.steamID);
    
    // Get the active tab from URL params or sessionStorage
    const getInitialActiveTab = () => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam) return tabParam;
        
        const storedTab = sessionStorage.getItem(`player_${steamId}_tab`);
        return storedTab || 'stat'; // Default to 'stat' if nothing is stored
    };

    const [activeTab, setActiveTab] = useState(getInitialActiveTab());

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        
        // Update URL with the new tab
        const params = new URLSearchParams(location.search);
        params.set('tab', tabId);
        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
        
        // Store in sessionStorage
        sessionStorage.setItem(`player_${steamId}_tab`, tabId);
    };

    useEffect(() => {
        const fetchTeam = async () => {
            if (!steamId) return;

            try {
                const season = await getLatestSeason();
                const playerStat = await fetchPlayerBySteamIdAndSeason(steamId, season);
                if (!playerStat) return;
                const team = await fetchCurrentTeam(steamId);
                setTeam(team);
            }
            catch (e) {
                setTeam(null);
            }
        };
        fetchTeam();
    }, [steamId]);

    // Update active tab if URL changes (for back button support)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam && tabParam !== activeTab) {
            setActiveTab(tabParam);
        }
    }, [location.search, activeTab]);

    return (
        <div className="container">
          {isLoading ? (
            <Loading />
          ) : (
            <div>
              <div className="row align-items-center">
                <div className="col-md-2 text-left">
                  {visitor?.steamID === steamId || admins.find((admin) => admin === Number(visitor?.steamID)) ? (
                    <Link to={`/player/${steamId}/edit`} className="btn btn-primary mt-2 mb-2">
                      Rediger
                    </Link>
                  ) : null}
                </div>
              </div>
      
              <div className="row justify-content-center">
                {hasFantasyTeam && hasPredicted ? (
                  <>
                    <div className="col-md-6 text-center">
                      <Link
                        to={`/fantasy/view/${steamId}/${currentSeason}`}
                        className="btn btn-primary mt-2 mb-2"
                      >
                        {playerData?.playerName} Fantasy Hold
                      </Link>
                    </div>
                    <div className="col-md-6 text-center">
                      <Link
                        to={`/standings/predict/${steamId}`}
                        className="btn btn-primary mt-2 mb-2"
                      >
                        Se Forudsigelse
                      </Link>
                    </div>
                  </>
                ) : hasFantasyTeam ? (
                  <div className="col-md-6 text-center">
                    <Link
                      to={`/fantasy/view/${steamId}/${currentSeason}`}
                      className="btn btn-primary mt-2 mb-2"
                    >
                      {playerData?.playerName} Fantasy Hold
                    </Link>
                  </div>
                ) : hasPredicted ? (
                  <div className="col-md-6 text-center">
                    <Link
                      to={`/standings/predict/${steamId}`}
                      className="btn btn-primary mt-2 mb-2"
                    >
                      Se Forudsigelse
                    </Link>
                  </div>
                ) : null}
              </div>
      
              <PlayerInfo playerData={playerData} teamData={team} />
            </div>
          )}

          <hr />
          <div className="text-center">
              <SocialMediaButtons playerData={playerData} steamId={steamId} />
          </div>
          <hr />
          <TabsComponent onTabChange={handleTabChange} activeTab={activeTab} />
          <hr />
          {activeTab === 'stat' && (
              <Suspense fallback={<Loading />}>
                  <PlayerStatsDisplay />
              </Suspense>
          )}
          {activeTab === 'team' && (
              <Suspense fallback={<Loading />}>
                  <PlayerTeams />
              </Suspense>
          )}
          {activeTab === 'matches' && (
              <Suspense fallback={<Loading />}>
                  <PlayerMatch />
              </Suspense>
          )}
          {activeTab === 'achievements' && (
              <Suspense fallback={<Loading />}>
                  <PlayerAchivementsDisplay />
              </Suspense>
          )}
          
          {/* Add CompareButton component */}
          {!isLoading && steamId && (
            <CompareButton playerSteamId={steamId} isDark={isDark} />
          )}
        </div>
    );
}

export default PlayerPage;